import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface LandingImage {
  id: string;
  section: string;
  image_url: string;
  alt_text: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export const useLandingImages = (section?: string) => {
  return useQuery({
    queryKey: ["landing-images", section],
    queryFn: async () => {
      let query = supabase
        .from("landing_images")
        .select("*")
        .order("sort_order", { ascending: true });

      if (section) {
        query = query.eq("section", section).eq("is_active", true);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as LandingImage[];
    },
  });
};

export const useHeroImage = () => {
  const { data: images, isLoading } = useLandingImages("hero");
  
  // Return the first active hero image
  const heroImage = images?.[0];
  
  return { heroImage, isLoading };
};

export const useLandingImageMutations = () => {
  const queryClient = useQueryClient();

  const uploadImage = useMutation({
    mutationFn: async ({ file, section, altText }: { file: File; section: string; altText?: string }) => {
      const fileExt = file.name.split(".").pop();
      const fileName = `${section}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("landing-images")
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from("landing-images")
        .getPublicUrl(fileName);

      // Get current max sort_order for this section
      const { data: existing } = await supabase
        .from("landing_images")
        .select("sort_order")
        .eq("section", section)
        .order("sort_order", { ascending: false })
        .limit(1);

      const nextOrder = existing?.[0]?.sort_order ?? 0 + 1;

      const { data, error } = await supabase
        .from("landing_images")
        .insert({
          section,
          image_url: publicUrl,
          alt_text: altText || null,
          sort_order: nextOrder,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["landing-images"] });
      toast.success("Immagine caricata con successo");
    },
    onError: (error) => {
      toast.error(`Errore upload: ${error.message}`);
    },
  });

  const updateImage = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<LandingImage> }) => {
      const { data, error } = await supabase
        .from("landing_images")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["landing-images"] });
      toast.success("Immagine aggiornata");
    },
    onError: (error) => {
      toast.error(`Errore: ${error.message}`);
    },
  });

  const deleteImage = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("landing_images")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["landing-images"] });
      toast.success("Immagine eliminata");
    },
    onError: (error) => {
      toast.error(`Errore: ${error.message}`);
    },
  });

  const reorderImages = useMutation({
    mutationFn: async (images: { id: string; sort_order: number }[]) => {
      const updates = images.map(({ id, sort_order }) =>
        supabase
          .from("landing_images")
          .update({ sort_order })
          .eq("id", id)
      );

      await Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["landing-images"] });
    },
  });

  return { uploadImage, updateImage, deleteImage, reorderImages };
};
